// client/src/components/pages/LexiStar/Replay/trips/tripMapImages.ts
//
// Images the Trips map draws itself (ST-00159), so it needs no glyphs endpoint (the
// satellite raster style has none) and looks the same on both basemaps: the numbered start
// badges of the day view, a disc in the trip's colour with its number, white ring and a dark
// hairline. Drawn on demand from `styleimagemissing`: the image name `tripbadge:<n>:<#colour>`
// says everything needed to draw it. Every other missing image is left alone.
// ST-00318: moved with the tab to Replay; the playback vehicle went with the small player
// (a trip plays in Replay itself).
import type { Map as MaplibreMap } from 'maplibre-gl';
import { BADGE_IMAGE_PREFIX, inkOn } from './tripsDay';

const PIXEL_RATIO = 2;

/** The badge for trip `n` in `color`, or null where there is no canvas (tests). */
export function drawTripBadge(n: number, color: string): ImageData | null {
  if (typeof document === 'undefined') return null;
  const label = String(n);
  const fontPx = 11;
  const h = 20;
  const c = document.createElement('canvas');
  const probe = c.getContext('2d');
  if (!probe) return null;
  probe.font = `700 ${fontPx}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  const w = Math.max(h, Math.ceil(probe.measureText(label).width) + 10);
  c.width = (w + 2) * PIXEL_RATIO;
  c.height = (h + 2) * PIXEL_RATIO;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  ctx.scale(PIXEL_RATIO, PIXEL_RATIO);
  const r = h / 2;
  const pill = (inset: number) => {
    ctx.beginPath();
    ctx.moveTo(1 + r, 1 + inset);
    ctx.lineTo(1 + w - r, 1 + inset);
    ctx.arc(1 + w - r, 1 + r, r - inset, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(1 + r, 1 + h - inset);
    ctx.arc(1 + r, 1 + r, r - inset, Math.PI / 2, (3 * Math.PI) / 2);
    ctx.closePath();
  };
  // Dark hairline (reads on the street map), white ring (reads on imagery), colour fill.
  pill(-0.5);
  ctx.fillStyle = 'rgba(15, 23, 42, 0.55)';
  ctx.fill();
  pill(0.5);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  pill(2.25);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.font = `700 ${fontPx}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = inkOn(color);
  ctx.fillText(label, 1 + w / 2, 1 + r + 0.5);
  return ctx.getImageData(0, 0, c.width, c.height);
}

/** `styleimagemissing` handler: draws a missing trip badge; ignores every other image. */
export function addMissingTripBadge(map: MaplibreMap, id: string): void {
  if (!id.startsWith(BADGE_IMAGE_PREFIX)) return;
  const [nText, color] = id.slice(BADGE_IMAGE_PREFIX.length).split(':');
  const n = Number(nText);
  if (!Number.isFinite(n) || !color) return;
  const image = drawTripBadge(n, color);
  if (!image) return;
  try {
    if (!map.hasImage(id)) map.addImage(id, image, { pixelRatio: PIXEL_RATIO });
  } catch {
    /* map disposed mid-load */
  }
}
